import { useState, useEffect } from 'react';
import { useSessionStore } from '@stores/sessionStore';
import { useTranslationStore } from '@stores/translationStore';
import { apiClient } from '@services/api';

interface Language {
  code: string;
  nameNative: string;
  direction: string;
}

interface LanguageSwitcherProps {
  variant?: 'default' | 'globe';
}

export default function LanguageSwitcher({ variant = 'default' }: LanguageSwitcherProps) {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { session, updateLanguage } = useSessionStore();
  const { clearCache } = useTranslationStore();

  useEffect(() => {
    loadLanguages();
  }, []);

  async function loadLanguages() {
    try {
      const response = await apiClient.get('/api/v1/languages');
      if (response.data.success) {
        setLanguages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load languages:', error);
    }
  }

  const currentLanguage = languages.find(l => l.code === session?.language);

  const handleSelect = async (code: string) => {
    setIsOpen(false);
    if (code !== session?.language) {
      // Store language in localStorage FIRST to ensure it persists before reload
      localStorage.setItem('gcxp_language', code);
      
      // Update document direction immediately for visual consistency
      const rtlLanguages = ['he', 'ar'];
      const dir = rtlLanguages.includes(code) ? 'rtl' : 'ltr';
      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', code);
      
      // Clear translation cache so fresh translations load after reload
      await clearCache();
      
      // MUST await the API call to update session on server before reload
      // Otherwise validateSession will return the old language
      try {
        await updateLanguage(code);
      } catch (err) {
        console.error('Failed to update language on server:', err);
        // Continue with reload anyway - localStorage has the new language
      }
      
      // Force reload to apply new language
      window.location.reload();
    }
  };

  if (languages.length <= 1) return null;

  return (
    <div className={`dropdown ${variant === 'globe' ? 'language-globe' : ''}`}>
      <button
        className={variant === 'globe' 
          ? 'btn btn-link language-globe-btn' 
          : 'btn btn-outline-secondary btn-sm dropdown-toggle'}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {variant === 'globe' ? (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span className="language-globe-label">{currentLanguage?.nameNative || session?.language}</span>
          </>
        ) : (
          currentLanguage?.nameNative || session?.language || 'Language'
        )}
      </button>
      
      {isOpen && (
        <ul className="dropdown-menu show" style={{ insetInlineEnd: 0 }}>
          {languages.map(lang => (
            <li key={lang.code}>
              <button
                className={`dropdown-item ${lang.code === session?.language ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                {lang.nameNative}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
